import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import { drawListStatusKeys } from './useDrawListStatus';

// --- Query Key Factory ---
export const drawOpsKeys = {
  all: ['draw-ops'] as const,
  config: (eventId: string) => [...drawOpsKeys.all, 'config', eventId] as const,
  availablePartners: (eventId: string) =>
    [...drawOpsKeys.all, 'available-partners', eventId] as const,
  status: (eventId: string) => [...drawOpsKeys.all, 'status', eventId] as const,
  // Conditional spread keeps invalidateQueries prefix matching working —
  // a trailing `undefined` would defeat React Query's partialDeepEqual.
  teams: (eventId: string, filters?: DrawTeamsFilters) =>
    filters
      ? ([...drawOpsKeys.all, 'teams', eventId, filters] as const)
      : ([...drawOpsKeys.all, 'teams', eventId] as const),
  daySheet: (eventId: string) =>
    [...drawOpsKeys.all, 'day-sheet', eventId] as const,
};

// --- Types ---
interface DrawConfig {
  id: string;
  event_id: string;
  draw_type: string;
  pick_enabled: boolean;
  max_picks?: number;
  [key: string]: unknown;
}

interface UpdateDrawConfigData {
  draw_type?: string;
  pick_enabled?: boolean;
  max_picks?: number;
  [key: string]: unknown;
}

interface PickPartnerData {
  registration_id: string;
  partner_registration_id: string;
}

interface ConfirmPickData {
  confirmed: boolean;
}

interface AvailablePartner {
  registration_id: string;
  user_profile_id: string;
  first_name: string;
  last_name: string;
  position: 'Header' | 'Heeler';
  handicap_rating?: number;
  [key: string]: unknown;
}

interface ExecuteDrawData {
  algorithm?: string;
  seed?: number;
}

interface DrawStatus {
  event_id: string;
  status: string;
  total_teams: number;
  locked: boolean;
  executed_at?: string;
  [key: string]: unknown;
}

interface DrawTeam {
  id: string;
  team_number: number;
  header_registration_id: string;
  heeler_registration_id: string;
  [key: string]: unknown;
}

interface DrawTeamsFilters {
  search?: string;
  round?: number;
  /** Rodeo events: scope synthesized teams to one performance_event. */
  performance_event_id?: string;
}

interface UpdateDrawTeamData {
  team_number?: number;
  [key: string]: unknown;
}

interface SwapTeamMembersData {
  team_a_id: string;
  team_b_id: string;
  position: 'Header' | 'Heeler';
}

interface LockDrawData {
  lock: boolean;
}

interface DaySheet {
  event_id: string;
  teams: Array<{
    team_number: number;
    header: string;
    heeler: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

// --- Query Hooks ---
export function useDrawConfig(eventId: string) {
  return useQuery({
    queryKey: drawOpsKeys.config(eventId),
    queryFn: () =>
      httpClient.get<DrawConfig>(`/draw-ops/event/${eventId}/config`),
    enabled: !!eventId,
  });
}

export function useAvailablePartners(eventId: string) {
  return useQuery({
    queryKey: drawOpsKeys.availablePartners(eventId),
    queryFn: () =>
      httpClient.get<AvailablePartner[]>(
        `/draw-ops/event/${eventId}/available-partners`,
      ),
    enabled: !!eventId,
  });
}

export function useDrawStatus(eventId: string) {
  return useQuery({
    queryKey: drawOpsKeys.status(eventId),
    queryFn: () =>
      httpClient.get<DrawStatus>(`/draw-ops/event/${eventId}/status`),
    enabled: !!eventId,
  });
}

export function useDrawTeams(eventId: string, filters?: DrawTeamsFilters) {
  const qs = new URLSearchParams();
  if (filters?.search) qs.set('search', filters.search);
  if (filters?.round) qs.set('round', String(filters.round));
  if (filters?.performance_event_id) qs.set('performance_event_id', filters.performance_event_id);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: drawOpsKeys.teams(eventId, filters),
    queryFn: () =>
      httpClient.get<DrawTeam[]>(`/draw-ops/event/${eventId}/teams${query}`),
    enabled: !!eventId,
  });
}

export function useDaySheet(eventId: string) {
  return useQuery({
    queryKey: drawOpsKeys.daySheet(eventId),
    queryFn: () =>
      httpClient.get<DaySheet>(`/draw-ops/event/${eventId}/day-sheet`),
    enabled: !!eventId,
  });
}

// --- Mutation Hooks ---
export function useUpdateDrawConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: UpdateDrawConfigData & { eventId: string }) =>
      httpClient.put<DrawConfig>(`/draw-ops/event/${eventId}/config`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsKeys.config(variables.eventId),
      });
    },
  });
}

export function usePickPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: PickPartnerData & { eventId: string }) =>
      httpClient.post(`/draw-ops/event/${eventId}/pick`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
    },
  });
}

export function useConfirmPick() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      registrationId,
      ...data
    }: ConfirmPickData & { eventId: string; registrationId: string }) =>
      httpClient.post(
        `/draw-ops/event/${eventId}/pick/${registrationId}/confirm`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
    },
  });
}

export function useExecuteDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: ExecuteDrawData & { eventId: string }) =>
      httpClient.post(`/draw-ops/event/${eventId}/execute`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
    },
  });
}

export function useRollbackDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post(`/draw-ops/event/${eventId}/rollback`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
    },
  });
}

export function useUpdateDrawTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      teamId,
      ...data
    }: UpdateDrawTeamData & { eventId: string; teamId: string }) =>
      httpClient.put<DrawTeam>(
        `/draw-ops/event/${eventId}/teams/${teamId}`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsKeys.teams(variables.eventId),
      });
    },
  });
}

export function useSwapTeamMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: SwapTeamMembersData & { eventId: string }) =>
      httpClient.post(`/draw-ops/event/${eventId}/teams/swap`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
    },
  });
}

export function useLockDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: LockDrawData & { eventId: string }) =>
      httpClient.post(`/draw-ops/event/${eventId}/lock`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsKeys.status(variables.eventId),
      });
    },
  });
}

// --- Placement / Capacity / Draft / Conflict / Publish Types ---

interface CapacityConfig {
  event_id: string;
  performances: PerformanceSlotConfig[];
  default_slot_limit: number;
  /** `rodeos.is_finals` — drives round labelling + hides the preference UI. */
  is_finals?: boolean;
}

interface PerformanceSlotConfig {
  performance_id: string;
  performance_name: string;
  is_slack: boolean;
  sort_order: number;
  slot_limit: number;
  current_count: number;
  start_datetime?: string | null;
  /** Finals rodeos: the round this performance runs, e.g. "Round 1". */
  round_label?: string | null;
}

interface PlacementResult {
  execution_id: string | null;
  event_id: string;
  entries: PlacedEntry[];
  unplaced: UnplacedEntry[];
  conflicts: ConflictItemData[];
  warnings: string[];
}

interface PlacedEntry {
  registration_id: string;
  user_profile_id: string;
  event_id: string;
  performance_id: string;
  draw_position: number;
  priority_position: number;
  manual_override: boolean;
  non_member: boolean;
}

interface UnplacedEntry {
  registration_id: string;
  user_profile_id: string;
  event_id: string;
  reason: string;
}

interface ConflictItemData {
  type: string;
  severity: string;
  message: string;
  details: Record<string, unknown>;
}

interface DraftExecution {
  execution_id: string;
  event_id: string;
  // 'none' | 'draft' | 'published' | 'finalized' — kept as `string` for
  // forward-compat with future statuses; runtime checks narrow.
  placement_status: string;
  entries: DraftEntry[];
  warnings: string[];
  executed_at: string;
}

interface DraftEntry {
  id: string;
  execution_id: string;
  registration_id: string;
  event_id: string;
  performance_id: string | null;
  draw_position: number;
  manual_override: boolean;
  is_placed: boolean;
  unplaced_reason: string | null;
}

interface ConflictReport {
  event_id: string;
  conflicts: ConflictItemData[];
  buddy_group_overlaps: any[];
  same_person_violations: any[];
  unplaceable_groups: any[];
  rough_stock_zero_entries: any[];
  relationship_graph: any[];
}

export interface RodeoConflictReport {
  rodeo_id: string;
  conflicts: ConflictItemData[];
  buddy_group_overlaps: any[];
  same_person_violations: any[];
  unplaceable_groups: any[];
  rough_stock_zero_entries: any[];
  relationship_graph: any[];
  by_event: Array<{ event_id: string; event_name: string; conflict_count: number }>;
}

// --- Extended Query Keys ---

export const drawOpsPlacementKeys = {
  capacity: (eventId: string) =>
    [...drawOpsKeys.all, 'capacity', eventId] as const,
  draft: (eventId: string) =>
    [...drawOpsKeys.all, 'draft', eventId] as const,
  conflicts: (eventId: string) =>
    [...drawOpsKeys.all, 'conflicts', eventId] as const,
  rodeoConflicts: (rodeoId: string) =>
    [...drawOpsKeys.all, 'rodeo-conflicts', rodeoId] as const,
  rodeoBuddyGroupsOverview: (rodeoId: string) =>
    [...drawOpsKeys.all, 'rodeo-buddy-groups-overview', rodeoId] as const,
  preview: (eventId: string) =>
    [...drawOpsKeys.all, 'preview', eventId] as const,
};

// --- Buddy Groups Overview Types ---

export interface BuddyGroupOverviewEntry {
  registration_id: string;
  event_id: string;
  event_name: string;
  performance_id: string | null;
  performance_name: string | null;
  is_split_offender: boolean;
}

export interface BuddyGroupOverviewMember {
  user_profile_id: string;
  display_name: string;
  entries: BuddyGroupOverviewEntry[];
}

export interface BuddyGroupOverviewItem {
  buddy_group_id: string;
  group_name: string | null;
  member_count: number;
  entry_count: number;
  placed_entry_count: number;
  has_split_performance: boolean;
  performance_ids: string[];
  performance_names: string[];
  members: BuddyGroupOverviewMember[];
}

export interface BuddyGroupOverviewReport {
  rodeo_id: string;
  buddy_groups: BuddyGroupOverviewItem[];
}

// --- Placement / Capacity Query Hooks ---

export function useCapacityConfig(eventId: string) {
  return useQuery({
    queryKey: drawOpsPlacementKeys.capacity(eventId),
    queryFn: () =>
      httpClient.get<CapacityConfig>(
        `/draw-ops/event/${eventId}/capacity`,
      ),
    enabled: !!eventId,
  });
}

export function useDraftDraw(eventId: string) {
  return useQuery({
    queryKey: drawOpsPlacementKeys.draft(eventId),
    queryFn: () =>
      httpClient.get<DraftExecution>(
        `/draw-ops/event/${eventId}/draft`,
      ),
    enabled: !!eventId,
  });
}

export function useDrawConflicts(eventId: string) {
  return useQuery({
    queryKey: drawOpsPlacementKeys.conflicts(eventId),
    queryFn: () =>
      httpClient.get<ConflictReport>(
        `/draw-ops/event/${eventId}/conflicts`,
      ),
    enabled: !!eventId,
  });
}

/**
 * Rodeo-wide conflict rollup for the Manage Draws list. Each conflict is
 * tagged with `details.event_id` so a click can deep-link to the right
 * per-event drilldown. Returns null-result when no rodeoId is provided
 * (e.g. the row in the list is a jackpot, not a rodeo).
 */
export function useDrawConflictsForRodeo(rodeoId: string | null | undefined) {
  return useQuery({
    queryKey: drawOpsPlacementKeys.rodeoConflicts(rodeoId ?? ''),
    queryFn: () =>
      httpClient.get<RodeoConflictReport>(
        `/draw-ops/rodeo/${rodeoId}/conflicts`,
      ),
    enabled: !!rodeoId,
  });
}

/**
 * Buddy-group inspection panel data for the Manage Draws list. Returns
 * one entry per buddy group in the rodeo, each with member-level
 * placement detail so the FE can render the expandable section and
 * color-code groups whose entries are split across performances.
 */
export function useRodeoBuddyGroupsOverview(rodeoId: string | null | undefined) {
  return useQuery({
    queryKey: drawOpsPlacementKeys.rodeoBuddyGroupsOverview(rodeoId ?? ''),
    queryFn: () =>
      httpClient.get<BuddyGroupOverviewReport>(
        `/draw-ops/rodeo/${rodeoId}/buddy-groups-overview`,
      ),
    enabled: !!rodeoId,
  });
}

// --- Placement / Capacity Mutation Hooks ---

export function useSetSlotLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      performanceId,
      ...data
    }: { eventId: string; performanceId: string; slot_limit: number; notes?: string }) =>
      httpClient.put(
        `/draw-ops/event/${eventId}/capacity/${performanceId}`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.capacity(variables.eventId),
      });
    },
  });
}

export function useRemoveSlotLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      performanceId,
    }: { eventId: string; performanceId: string }) =>
      httpClient.delete(
        `/draw-ops/event/${eventId}/capacity/${performanceId}`,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.capacity(variables.eventId),
      });
    },
  });
}

export function usePreviewPlacement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post<PlacementResult>(
        `/draw-ops/event/${eventId}/preview`,
      ),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.preview(eventId),
      });
    },
  });
}

export function useExecutePlacement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post<PlacementResult>(
        `/draw-ops/event/${eventId}/execute-placement`,
      ),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.draft(eventId),
      });
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.conflicts(eventId),
      });
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      // The Manage Draws list query lives under a different root key
      // (drawListStatusKeys) so drawOpsKeys.all does NOT reach it.
      // Without this the row badges (Draft/Published/Finalized) and
      // counts stay stale until the user manually reloads the page.
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// Reset-only: discard the current draft execution without generating a
// new one. Sister action to useExecutePlacement (which is reset-then-
// regenerate). Backend route is idempotent — returns { deleted: false }
// when there is no draft to remove. Only touches the draft execution
// row; never published/finalized rows or live registrations.
export interface ResetDraftResult {
  success: boolean;
  deleted: boolean;
  execution_id?: string;
}

export function useResetDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post<ResetDraftResult>(
        `/draw-ops/event/${eventId}/draft/reset`,
      ),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.draft(eventId),
      });
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.conflicts(eventId),
      });
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

export function useUpdateDraftEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      entryId,
      ...body
    }: {
      eventId: string;
      entryId: string;
      performance_id?: string | null;
      draw_position?: number;
      is_placed?: boolean;
    }) =>
      httpClient.patch(
        `/draw-ops/event/${eventId}/draft/entry/${entryId}`,
        body,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.draft(variables.eventId),
      });
      // The day sheet now derives from the live draw execution (same source
      // as the event-details Draw Results), so reordering/moving an entry
      // must refresh it too — otherwise it stays stale until remount.
      qc.invalidateQueries({
        queryKey: drawOpsKeys.daySheet(variables.eventId),
      });
      // Moving an entry between performances changes placed_count /
      // not_drawn_count on the Manage Draws list row, so refresh it.
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

export function useReorderDraftPerformance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      performanceId,
      entryIds,
    }: {
      eventId: string;
      performanceId: string;
      entryIds: string[];
    }) =>
      httpClient.patch(
        `/draw-ops/event/${eventId}/draft/performance/${performanceId}/order`,
        { entry_ids: entryIds },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.draft(variables.eventId),
      });
      // Keep the live-sourced day sheet in sync with the new order.
      qc.invalidateQueries({
        queryKey: drawOpsKeys.daySheet(variables.eventId),
      });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// Finals run-order picker on the draw board. 'draw' is the client-side
// no-op ("leave the draw as drawn"); the other four are sent to the backend.
// Mirrors backend RunOrderMode (services/draw/run-order.ts) — keep aligned.
export type DrawRunOrderMode = 'draw' | 'ytd_desc' | 'ytd_asc' | 'current_desc' | 'current_asc';
export type WinningsReorderMode = Exclude<DrawRunOrderMode, 'draw'>;

export interface WinningsReorderResult {
  success: boolean;
  mode: WinningsReorderMode;
  round_number: number | null;
  entries: Array<{
    id: string;
    registration_id: string;
    draw_position: number;
    manual_override: true;
  }>;
  /** registration_id → the dollar figure the row was ordered by. */
  amounts: Record<string, number>;
}

/**
 * Finals only: one-shot rewrite of a performance's draw positions by YTD or
 * current-rodeo winnings. Same invalidations as the drag reorder — the
 * draft, the live-sourced day sheet and the Manage Draws list all change.
 */
export function useReorderDraftPerformanceByWinnings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      performanceId,
      mode,
    }: {
      eventId: string;
      performanceId: string;
      mode: WinningsReorderMode;
    }) =>
      httpClient.post<WinningsReorderResult>(
        `/draw-ops/event/${eventId}/draft/performance/${performanceId}/reorder-by-winnings`,
        { mode },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsPlacementKeys.draft(variables.eventId),
      });
      qc.invalidateQueries({
        queryKey: drawOpsKeys.daySheet(variables.eventId),
      });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// The heeler half of a team roping entry. Mirrors backend
// PublishPreviewTeamPartnerSchema — keep aligned.
export interface PublishPreviewTeamPartner {
  registration_id: string;
  user_profile_id: string | null;
  display_name: string;
  non_member: boolean;
  position: string | null;
  has_payment: boolean;
  payment_amount: number;
  refund_amount_estimate: number;
  retained_fee_estimate: number;
}

// One row in the publish-preview response. Mirrors backend
// PublishPreviewEntrySchema (draw-placement-schemas.ts) — keep aligned.
export interface PublishPreviewEntry {
  registration_id: string;
  user_profile_id: string | null;
  display_name: string;
  non_member: boolean;
  unplaced_reason: string | null;
  is_draw_out_pref: boolean;
  has_payment: boolean;
  payment_amount: number;
  refund_amount_estimate: number;
  retained_fee_estimate: number;
  is_team_primary: boolean;
  team_partner_name: string | null;
  // The heeler half of a team entry — cancelling this row cancels both.
  // Null for solo entries.
  team_partner: PublishPreviewTeamPartner | null;
}

export interface PublishPreview {
  unplaced: PublishPreviewEntry[];
  placed_count: number;
}

/**
 * Lazy drawn-out preview: returns the list of unplaced/drawn-out
 * registrations that would be cancelled (with refund estimates) if the
 * admin runs the Cancel Drawn-Out flow right now. Gated on `enabled`
 * so it doesn't auto-fire — the FE only calls this when the admin
 * opens the cancel modal. Endpoint is shared with the (now-removed)
 * pre-publish modal; backend re-derives the set from current draft
 * state, so the list stays accurate regardless of which lifecycle
 * state the draw is in (published or finalized).
 */
export function useDrawPublishPreview(eventId: string, enabled = false) {
  return useQuery<PublishPreview>({
    queryKey: [...drawOpsKeys.all, 'drawn-out-preview', eventId] as const,
    queryFn: () =>
      httpClient.get<PublishPreview>(
        `/draw-ops/event/${eventId}/publish/preview`,
      ),
    enabled: !!eventId && enabled,
    // Always re-fetch on each open of the modal so the admin sees
    // current state (someone could have been placed since last check).
    staleTime: 0,
    gcTime: 0,
  });
}

export function usePublishDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      notes,
    }: { eventId: string; notes?: string }) =>
      httpClient.post(`/draw-ops/event/${eventId}/publish`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

/**
 * Per-event drawn-out status: how many drawn-out registrations are
 * still uncancelled on this event's latest draw, plus the placement
 * state. Powers the Cancel Drawn-Out button's enabled/disabled state
 * on the draw page — only renders the button when finalized + has
 * uncancelled rows. Polled-ish: same staleTime as the rest of the
 * draw page so it stays in sync with the other surfaces without
 * adding chatter.
 */
export interface DrawnOutStatusResponse {
  /**
   * True when the Cancel Drawn-Out flow has work to do — either real
   * drawn-out entries or partnerless team-roping rows that were never
   * paired. The two cohorts now share the same cancel button.
   */
  has_uncancelled: boolean;
  /** Union of drawn-out + partnerless TR cohorts (badge total). */
  count: number;
  /** Drawn-out / unplaced subset of `count`. */
  drawn_out_count?: number;
  /** Partnerless team-roping subset of `count`. */
  partnerless_count?: number;
  placement_status: string | null;
  success?: boolean;
}

export function useDrawnOutStatus(eventId: string, enabled = true) {
  return useQuery<DrawnOutStatusResponse>({
    queryKey: [...drawOpsKeys.all, 'drawn-out-status', eventId] as const,
    queryFn: () =>
      httpClient.get<DrawnOutStatusResponse>(
        `/draw-ops/event/${eventId}/drawn-out-status`,
      ),
    enabled: !!eventId && enabled,
    staleTime: 30_000,
  });
}

/**
 * Manually cancel every drawn-out / unplaced registration on this
 * event's latest draw. Use AFTER finalize, typically AFTER the
 * 24-hour call-in window many orgs grant drawn-out contestants. The
 * backend handles refunds (minus per-event transaction fee) via the
 * registration-payment cascade.
 */
export function useCancelDrawnOutEntries() {
  const qc = useQueryClient();
  return useMutation<
    { cancelled_count: number; registration_ids: string[]; success: boolean },
    Error,
    // `refundFeeRegistrationIds` — preview entries (by primary registration_id)
    // for which the admin chose to ALSO refund the transaction fee. Entries
    // omitted keep the fee retained (the default). Optional for backward
    // compatibility with any caller that just wants the standard policy.
    { eventId: string; refundFeeRegistrationIds?: string[] }
  >({
    mutationFn: ({ eventId, refundFeeRegistrationIds }) =>
      httpClient.post(
        `/draw-ops/event/${eventId}/cancel-drawn-out`,
        { refund_fee_registration_ids: refundFeeRegistrationIds ?? [] },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

/**
 * Admin/delegate dashboard: list every event whose draw is finalized
 * AND still has drawn-out registrations needing cancellation. Drives
 * the dashboard warning card.
 */
export interface FinalizedDrawnOutEvent {
  event_id: string;
  event_name: string;
  rodeo_id: string;
  rodeo_name: string;
  organization_id: string | null;
  finalized_at: string | null;
  drawn_out_count: number;
}

export function useFinalizedWithDrawnOut(enabled = true) {
  return useQuery<{ events: FinalizedDrawnOutEvent[]; success?: boolean }>({
    queryKey: [...drawOpsKeys.all, 'finalized-with-drawn-out'] as const,
    queryFn: () =>
      httpClient.get<{ events: FinalizedDrawnOutEvent[]; success?: boolean }>(
        '/draw-ops/finalized-with-drawn-out',
      ),
    enabled,
    // Refresh on every dashboard mount — admins will want the latest
    // state after they cancel from a deep link and come back.
    staleTime: 30_000,
  });
}

export function useUnpublishDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post(`/draw-ops/event/${eventId}/unpublish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// Finalize: explicit gate distinct from publish — published → finalized.
// Non-destructive (daysheet entries were written at publish time;
// event_registrations is never touched). Reserved as the gate for
// downstream Task E surfaces (notifications, resend history, live-results).
export function useFinalizeDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post(`/draw-ops/event/${eventId}/finalize`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// Reverse finalize: finalized → published. Allowed before scoring starts.
export function useUnfinalizeDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      httpClient.post(`/draw-ops/event/${eventId}/unfinalize`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// ─── Draw Notification Template (Task E follow-up #1) ──────────────────

export interface DrawNotificationVariable {
  name: string;
  description: string;
}

export interface DrawNotificationConditional {
  name: string;
  description: string;
}

export interface DrawNotificationTemplate {
  event_id: string;
  // Templates are now org-scoped (one row per organization, reused by
  // every event under that org). `null` when the event's rodeo has no
  // primary organization configured.
  organization_id: string | null;
  subject_template: string;
  body_template: string;
  last_sent_at: string | null;
  last_sent_by: string | null;
  last_sent_count: number | null;
  is_default: boolean;
  variables: DrawNotificationVariable[];
  conditionals: DrawNotificationConditional[];
}

// One entry the send would reach. One row per entry, so a contestant
// entered twice in the same event appears twice (different performance /
// draw position). The modal toggles select by `event_registration_id`.
export interface DrawNotificationRecipient {
  event_registration_id: string;
  user_profile_id: string;
  contestant_full_name: string;
  email: string | null;
  performance_name: string;
  draw_position: string;
}

export interface SendNotificationsResult {
  recipients_total: number;
  inapp_sent: number;
  email_sent: number;
  email_skipped_optout: number;
  email_skipped_no_email: number;
  email_failed: number;
  last_sent_at: string;
}

export const drawOpsNotificationKeys = {
  template: (eventId: string) =>
    [...drawOpsKeys.all, 'notification-template', eventId] as const,
  history: (eventId: string) =>
    [...drawOpsKeys.all, 'notification-history', eventId] as const,
  historyRecipients: (eventId: string, sendId: string) =>
    [...drawOpsKeys.all, 'notification-history', eventId, sendId] as const,
  recipients: (eventId: string) =>
    [...drawOpsKeys.all, 'notification-recipients', eventId] as const,
};

// Recipients the next send would reach — used by the send-confirmation
// modal to render the per-recipient toggle list. Pass `enabled` so the
// query only fires while the modal is open.
export function useDrawNotificationRecipients(
  eventId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: drawOpsNotificationKeys.recipients(eventId),
    queryFn: () =>
      httpClient.get<{ recipients: DrawNotificationRecipient[]; success: boolean }>(
        `/draw-ops/event/${eventId}/notification-recipients`,
      ),
    enabled: !!eventId && enabled,
  });
}

export function useDrawNotificationTemplate(
  eventId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: drawOpsNotificationKeys.template(eventId),
    queryFn: () =>
      httpClient.get<DrawNotificationTemplate>(
        `/draw-ops/event/${eventId}/notification-template`,
      ),
    enabled: !!eventId && (options?.enabled ?? true),
  });
}

export function useSaveDrawNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      subject_template,
      body_template,
    }: {
      eventId: string;
      subject_template: string;
      body_template: string;
    }) =>
      httpClient.put<DrawNotificationTemplate>(
        `/draw-ops/event/${eventId}/notification-template`,
        { subject_template, body_template },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: drawOpsNotificationKeys.template(variables.eventId),
      });
    },
  });
}

// Send accepts optional one-off overrides. Omit both fields to send
// with the saved org template; provide either to do a one-off dispatch
// that does NOT persist to the org template.
export function useSendDrawNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      subject_template,
      body_template,
      recipient_ids,
    }: {
      eventId: string;
      subject_template?: string;
      body_template?: string;
      // Selected entries (event_registration_id) to notify. Omit to send
      // to everyone in the draw.
      recipient_ids?: string[];
    }) =>
      httpClient.post<SendNotificationsResult>(
        `/draw-ops/event/${eventId}/send-notifications`,
        { subject_template, body_template, recipient_ids },
        // Non-idempotent: the backend dispatches one in-app insert + one
        // external Resend email per recipient sequentially, which for a
        // full draw (~36 contestants) routinely runs past the client's
        // default 10s timeout. An auto-retry on that timeout fires a
        // SECOND full `initial` send (the aborted first attempt keeps
        // running server-side to completion), so each click fanned out
        // into multiple duplicate batches — the "sent 5× over" bug. Never
        // retry, and give the batch room to finish so the admin sees the
        // real result instead of a premature abort.
        { retries: 0, timeout: 120000 },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: drawOpsNotificationKeys.template(vars.eventId),
      });
      qc.invalidateQueries({
        queryKey: drawOpsNotificationKeys.history(vars.eventId),
      });
      // Refresh the Manage Draws list so the "Notifications sent" badge
      // lights up immediately after the first send (and stays accurate
      // through subsequent re-sends).
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// ─── Notification send history ────────────────────────────────────────────

export type SendType = 'initial' | 'resend_failed' | 'resend_all' | 'resend_selected';

export interface SendHistoryEntry {
  id: string;
  draw_execution_id: string;
  parent_send_id: string | null;
  send_type: SendType;
  subject_template_used: string;
  body_template_used: string;
  used_override: boolean;
  sent_at: string;
  sent_by: string | null;
  sent_by_name: string | null;
  recipient_count: number;
  inapp_sent: number;
  email_sent: number;
  email_skipped_optout: number;
  email_skipped_no_email: number;
  email_failed: number;
}

export interface SendHistoryRecipient {
  id: string;
  send_id: string;
  user_profile_id: string | null;
  event_registration_id: string | null;
  email_at_send: string | null;
  inapp_status: 'sent' | 'failed';
  email_status: 'sent' | 'opted_out' | 'no_email' | 'failed' | 'skipped';
  email_error: string | null;
  display_name_at_send: string | null;
  performance_name_at_send: string | null;
  draw_position_at_send: string | null;
}

export function useDrawNotificationHistory(
  eventId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: drawOpsNotificationKeys.history(eventId),
    queryFn: () =>
      httpClient.get<{ history: SendHistoryEntry[] }>(
        `/draw-ops/event/${eventId}/notification-history`,
      ),
    enabled: !!eventId && (options?.enabled ?? true),
  });
}

export function useDrawNotificationHistoryRecipients(
  eventId: string,
  sendId: string | null,
) {
  return useQuery({
    queryKey: drawOpsNotificationKeys.historyRecipients(eventId, sendId ?? ''),
    queryFn: () =>
      httpClient.get<{ recipients: SendHistoryRecipient[] }>(
        `/draw-ops/event/${eventId}/notification-history/${sendId}`,
      ),
    enabled: !!eventId && !!sendId,
  });
}

// ─── Draw-results visibility (live tab) ────────────────────────────────────

export interface DrawResultsVisibility {
  event_id: string;
  visible: boolean;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
}

export interface PublicDrawResultsEntry {
  id: string;
  performance_id: string | null;
  draw_position: number;
  display_name: string | null;
  team_partner_name: string | null;
  team_position: string | null;
  team_partner_position: string | null;
  is_team: boolean;
}

export interface PublicDrawResults {
  event_id: string;
  event_name: string;
  rodeo_name: string;
  placement_status: 'published' | 'finalized';
  performances: Array<{
    performance_id: string;
    performance_name: string;
    is_slack: boolean;
    sort_order: number;
    entries: PublicDrawResultsEntry[];
  }>;
  last_applied_at: string | null;
}

export const drawOpsResultsKeys = {
  visibility: (eventId: string) =>
    [...drawOpsKeys.all, 'draw-results-visibility', eventId] as const,
  publicResults: (eventId: string) =>
    [...drawOpsKeys.all, 'public-draw-results', eventId] as const,
};

export function useDrawResultsVisibility(eventId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: drawOpsResultsKeys.visibility(eventId),
    queryFn: () =>
      httpClient.get<DrawResultsVisibility>(
        `/draw-ops/event/${eventId}/draw-results-visibility`,
      ),
    enabled: !!eventId && (options?.enabled ?? true),
  });
}

export function useSetDrawResultsVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, visible }: { eventId: string; visible: boolean }) =>
      httpClient.put<DrawResultsVisibility>(
        `/draw-ops/event/${eventId}/draw-results-visibility`,
        { visible },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: drawOpsResultsKeys.visibility(vars.eventId) });
      qc.invalidateQueries({ queryKey: drawOpsResultsKeys.publicResults(vars.eventId) });
      // Refresh the Manage Draws list so the "Live / showing to
      // contestants" badge flips on/off as the admin toggles
      // visibility — without this the list shows stale state until
      // the next 15-second staleTime refresh.
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

export function usePublicDrawResults(eventId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: drawOpsResultsKeys.publicResults(eventId),
    queryFn: () =>
      httpClient.get<PublicDrawResults>(`/draw-ops/event/${eventId}/public-draw-results`),
    // Backend 404s when results aren't visible — `retry:false` makes
    // that land instantly so the contestant UI doesn't burn React
    // Query's default 3-retry budget waiting on a "no, this admin
    // hasn't toggled it on" answer.
    retry: false,
    enabled: !!eventId && (options?.enabled ?? true),
  });
}

export function useResendDrawNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      parent_send_id,
      mode,
      selected_recipient_ids,
      subject_template,
      body_template,
    }: {
      eventId: string;
      parent_send_id: string;
      mode: 'failed' | 'all' | 'selected';
      selected_recipient_ids?: string[];
      subject_template?: string;
      body_template?: string;
    }) =>
      httpClient.post<SendNotificationsResult>(
        `/draw-ops/event/${eventId}/notification-resend`,
        {
          parent_send_id,
          mode,
          selected_recipient_ids,
          subject_template,
          body_template,
        },
        // Same non-idempotent, slow-batch profile as the initial send
        // above — disable auto-retry and extend the timeout so a timed-out
        // POST can't silently fire a duplicate resend batch.
        { retries: 0, timeout: 120000 },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: drawOpsNotificationKeys.history(vars.eventId),
      });
      qc.invalidateQueries({
        queryKey: drawOpsNotificationKeys.template(vars.eventId),
      });
    },
  });
}

export interface DrawNotificationPreviewResult {
  subject: string;
  body_html: string;
  body_text: string;
  sample_context: Record<string, string>;
}

// Preview accepts in-progress textarea values so the admin can verify
// edits before saving. Omit both fields to preview the saved/default row.
export function usePreviewDrawNotificationTemplate() {
  return useMutation({
    mutationFn: ({
      eventId,
      subject_template,
      body_template,
    }: {
      eventId: string;
      subject_template?: string;
      body_template?: string;
    }) =>
      httpClient.post<DrawNotificationPreviewResult>(
        `/draw-ops/event/${eventId}/notification-template/preview`,
        { subject_template, body_template },
      ),
  });
}

// ─── Rodeo-level bulk draft draw actions ──────────────────────────────────
//
// Two header-level admin actions on the rodeo draw management page:
//   - Reset All Draft Draws         (wipes every DRAFT event's draft)
//   - Reset & Run Initial Draw       (wipes + re-runs placement pipeline)
//
// Both operate only on events whose current draw is in DRAFT state;
// published / finalized draws are skipped server-side. The preview hook
// powers the confirmation modal's affected-events list.

export interface BulkDraftAffectedEvent {
  event_id: string;
  event_name: string;
  execution_id: string;
  entry_count: number;
  placed_count: number;
}

export interface BulkDraftSkippedPublishedFinalized {
  event_id: string;
  event_name: string;
  placement_status: 'published' | 'finalized';
}

export interface BulkDraftSkippedNoExecution {
  event_id: string;
  event_name: string;
}

export interface BulkDraftPreviewReport {
  rodeo_id: string;
  rodeo_name: string | null;
  affected_events: BulkDraftAffectedEvent[];
  skipped_published_or_finalized: BulkDraftSkippedPublishedFinalized[];
  skipped_no_execution: BulkDraftSkippedNoExecution[];
  success?: boolean;
}

export interface BulkDraftActionResult {
  event_id: string;
  event_name: string;
  status: 'success' | 'failed' | 'skipped';
  error?: string;
  new_entry_count?: number;
  new_placed_count?: number;
  warnings?: string[];
}

export interface BulkDraftActionReport {
  rodeo_id: string;
  total_events_requested: number;
  successful: number;
  failed: number;
  skipped: number;
  results: BulkDraftActionResult[];
  success?: boolean;
}

export const drawOpsBulkDraftKeys = {
  preview: (rodeoId: string, mode: 'reset' | 'run-initial' = 'reset') =>
    [...drawOpsKeys.all, 'rodeo-bulk-draft-preview', rodeoId, mode] as const,
};

/**
 * Preview the events the bulk action would touch. Lazy — only fires when
 * the confirmation modal is open. `staleTime: 0` so the admin always
 * sees the latest state (someone could have published mid-session).
 *
 * `mode` controls how events are bucketed:
 *   - `'reset'` (default): DRAFT goes into `affected_events`,
 *     NOT_STARTED into `skipped_no_execution`.
 *   - `'run-initial'`: NOT_STARTED goes into `affected_events`, DRAFT
 *     into `skipped_no_execution` (because run-initial deliberately
 *     doesn't touch existing drafts — admin must reset first).
 */
export function useRodeoDraftEventsPreview(
  rodeoId: string | null | undefined,
  enabled = false,
  mode: 'reset' | 'run-initial' = 'reset',
) {
  return useQuery<BulkDraftPreviewReport>({
    queryKey: drawOpsBulkDraftKeys.preview(rodeoId ?? '', mode),
    queryFn: () =>
      httpClient.get<BulkDraftPreviewReport>(
        `/draw-ops/rodeo/${rodeoId}/draft-events-preview?mode=${mode}`,
      ),
    enabled: !!rodeoId && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

// Bulk draw mutations process every event in a rodeo sequentially
// through the placement pipeline. With ~12 events that comfortably
// exceeds the default 10s HTTP timeout. Worse, the default retry
// policy would fire a second request while the first is still
// running server-side, and the retry would see events that the first
// call had just moved to DRAFT and report them as "skipped" — even
// though the first call succeeded for every event. Use a long
// timeout (well above any plausible run time) and `retries: 0` so
// the response the user sees always reflects the authoritative
// backend outcome.
const BULK_DRAW_REQUEST_OPTS = {
  timeout: 5 * 60 * 1000, // 5 minutes
  retries: 0,
} as const;

export function useResetRodeoDraftDraws() {
  const qc = useQueryClient();
  return useMutation<BulkDraftActionReport, Error, { rodeoId: string }>({
    mutationFn: ({ rodeoId }) =>
      httpClient.post<BulkDraftActionReport>(
        `/draw-ops/rodeo/${rodeoId}/reset-draft-draws`,
        {},
        BULK_DRAW_REQUEST_OPTS,
      ),
    onSuccess: () => {
      // Per-event drafts, conflicts, status badges, list rollups all
      // change as a result of this action — invalidate broadly.
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

/**
 * Run the initial draw for every NOT_STARTED event in a rodeo. DRAFT
 * events are skipped — admin must explicitly reset them first if they
 * want a re-run. The product flow is "click this button to draw all
 * the brand-new events at once" without trampling sandbox tweaks
 * delegates may have made to draft events.
 */
export function useRunInitialRodeoDraws() {
  const qc = useQueryClient();
  return useMutation<BulkDraftActionReport, Error, { rodeoId: string }>({
    mutationFn: ({ rodeoId }) =>
      httpClient.post<BulkDraftActionReport>(
        `/draw-ops/rodeo/${rodeoId}/run-initial-draws`,
        {},
        BULK_DRAW_REQUEST_OPTS,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
      qc.invalidateQueries({ queryKey: drawListStatusKeys.all });
    },
  });
}

// ─── Capacity Matrix (admin UI) ────────────────────────────────────────────

export interface CapacityMatrixPerformance {
  performance_id: string;
  performance_name: string;
  is_linked: boolean;
  per_perf_event_override: number | null;
  draw_capacity_override: number | null;
  effective_slot_limit: number;
}

export interface CapacityMatrixSlack {
  performance_id: string;
  performance_name: string;
  draw_capacity_override: number | null;
  effective_slot_limit: number;
}

export interface CapacityMatrixEvent {
  event_id: string;
  event_name: string;
  max_contestants: number | null;
  derived_default_slot_limit: number;
  performances: CapacityMatrixPerformance[];
  slack: CapacityMatrixSlack | null;
}

export interface CapacityMatrix {
  rodeo_id: string;
  performances: Array<{
    performance_id: string;
    performance_name: string;
    is_slack: boolean;
    sort_order: number;
  }>;
  events: CapacityMatrixEvent[];
}

export interface SaveEventCapacityPayload {
  max_contestants: number | null;
  performances?: Array<{
    performance_id: string;
    per_perf_event_override: number | null;
    draw_capacity_override: number | null;
  }>;
  slack?: number | null;
}

export const drawOpsCapacityMatrixKeys = {
  matrix: (rodeoId: string) =>
    [...drawOpsKeys.all, 'capacity-matrix', rodeoId] as const,
};

export function useCapacityMatrix(rodeoId: string | null | undefined) {
  return useQuery({
    queryKey: drawOpsCapacityMatrixKeys.matrix(rodeoId ?? ''),
    queryFn: () =>
      httpClient.get<CapacityMatrix & { success: boolean }>(
        `/draw-ops/rodeo/${rodeoId}/capacity-matrix`,
      ),
    enabled: !!rodeoId,
  });
}

export function useSaveEventCapacity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      payload,
    }: { eventId: string; payload: SaveEventCapacityPayload }) =>
      httpClient.put(`/draw-ops/event/${eventId}/capacity-matrix`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: drawOpsKeys.all });
    },
  });
}
